import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import AudiencePicker from './AudiencePicker';
import { inspectPhaseAudience } from '../utils/calendarAudienceUtils';

const EVENT_TYPES = [
  { value: 'school-event', label: 'School Event' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'parent-conference', label: 'Parent Conference' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'exam', label: 'Exam' },
  { value: 'sports', label: 'Sports' },
  { value: 'custom', label: 'Custom' },
];

const COLOR_OPTIONS = [
  { value: null, label: 'Auto (by type)' },
  { value: 'primary', label: 'Teal' },
  { value: 'secondary', label: 'Purple' },
  { value: 'accent', label: 'Magenta' },
  { value: 'success', label: 'Green' },
  { value: 'warning', label: 'Amber' },
  { value: 'error', label: 'Red' },
  { value: 'info', label: 'Blue' },
];

const defaultAudience = { scope: 'everyone' };

function toInputDate(val) {
  if (!val) return '';
  const d = val.toDate ? val.toDate() : new Date(val);
  return format(d, 'yyyy-MM-dd');
}

function toInputTime(val) {
  if (!val) return '';
  const d = val.toDate ? val.toDate() : new Date(val);
  return format(d, 'HH:mm');
}

/**
 * CalendarEventModal - Create or edit a calendar event.
 *
 * Props:
 *   event: existing event to edit (null for create)
 *   initialDate: pre-fill start date when creating from day click
 *   onSave: (eventData) => Promise<void>
 *   onDelete: (eventId) => Promise<void>
 *   onClose: () => void
 */
export default function CalendarEventModal({ event, initialDate, onSave, onDelete, onClose }) {
  const isEditing = !!event;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('09:00');
  const [allDay, setAllDay] = useState(false);
  const [type, setType] = useState('school-event');
  const [color, setColor] = useState(null);
  const [audience, setAudience] = useState(defaultAudience);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form from existing event or initialDate
  useEffect(() => {
    if (event) {
      setTitle(event.title || '');
      setDescription(event.description || '');
      setStartDate(toInputDate(event.startDate));
      setStartTime(toInputTime(event.startDate));
      setEndDate(toInputDate(event.endDate));
      setEndTime(toInputTime(event.endDate));
      setAllDay(event.allDay || false);
      setType(event.type || 'school-event');
      setColor(event.color || null);
      setAudience(event.audience || defaultAudience);
    } else {
      const dateStr = format(initialDate || new Date(), 'yyyy-MM-dd');
      setStartDate(dateStr);
      setEndDate(dateStr);
    }
  }, [event, initialDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) { setError('Title is required'); return; }
    if (!startDate) { setError('Start date is required'); return; }
    if (!endDate) { setError('End date is required'); return; }

    const selectionField = {
      phases: inspectPhaseAudience(audience).representation === 'legacy' ? null : 'phaseIds',
      grades: 'gradeIds',
      classes: 'classIds',
      committees: 'committeeIds',
    }[audience.scope];
    if (selectionField && !audience[selectionField]?.length) {
      setError('Choose at least one audience, or use Whole school.');
      return;
    }

    const buildDateTime = (date, time) => {
      if (allDay) return new Date(`${date}T00:00:00`);
      return new Date(`${date}T${time || '00:00'}:00`);
    };

    const start = buildDateTime(startDate, startTime);
    const end = allDay
      ? new Date(`${endDate}T23:59:59`)
      : buildDateTime(endDate, endTime);

    if (end < start) { setError('End date must be after start date'); return; }

    setSaving(true);
    try {
      await onSave({
        ...(event ? { id: event.id } : {}),
        title: title.trim(),
        description: description.trim(),
        startDate: start,
        endDate: end,
        allDay,
        type,
        color,
        audience,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event?.id || !onDelete) return;
    if (!window.confirm('Delete this event?')) return;
    setSaving(true);
    try {
      await onDelete(event.id);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <dialog
      open
      className="modal modal-open"
      aria-labelledby="calendar-event-modal-title"
      aria-modal="true"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="modal-box max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 id="calendar-event-modal-title" className="font-bold text-lg mb-4">
          {isEditing ? 'Edit Event' : 'New Event'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="alert alert-error text-sm py-2">
              {error}
            </div>
          )}

          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="calendar-event-title" className="label text-sm font-medium">Title</label>
            <input
              id="calendar-event-title"
              name="title"
              type="text"
              className="input input-bordered w-full"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Event title"
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="calendar-event-description" className="label text-sm font-medium">Description</label>
            <textarea
              id="calendar-event-description"
              name="description"
              className="textarea textarea-bordered w-full"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>

          {/* All Day toggle */}
          <label htmlFor="calendar-event-all-day" className="label cursor-pointer justify-start gap-3">
            <input
              id="calendar-event-all-day"
              name="allDay"
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={allDay}
              onChange={e => setAllDay(e.target.checked)}
            />
            <span className="label-text">All day event</span>
          </label>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="calendar-event-start-date" className="label text-sm font-medium">Start date</label>
              <input
                id="calendar-event-start-date"
                name="startDate"
                type="date"
                className="input input-bordered w-full"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  if (!endDate || e.target.value > endDate) setEndDate(e.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="calendar-event-end-date" className="label text-sm font-medium">End date</label>
              <input
                id="calendar-event-end-date"
                name="endDate"
                type="date"
                className="input input-bordered w-full"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
          </div>

          {/* Times (hidden if all-day) */}
          {!allDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="calendar-event-start-time" className="label text-sm font-medium">Start time</label>
                <input
                  id="calendar-event-start-time"
                  name="startTime"
                  type="time"
                  className="input input-bordered w-full"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="calendar-event-end-time" className="label text-sm font-medium">End time</label>
                <input
                  id="calendar-event-end-time"
                  name="endTime"
                  type="time"
                  className="input input-bordered w-full"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Type and Color */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="calendar-event-type" className="label text-sm font-medium">Type</label>
              <select
                id="calendar-event-type"
                name="type"
                className="select select-bordered w-full"
                value={type}
                onChange={e => setType(e.target.value)}
              >
                {EVENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="calendar-event-colour" className="label text-sm font-medium">Colour</label>
              <select
                id="calendar-event-colour"
                name="colour"
                className="select select-bordered w-full"
                value={color || ''}
                onChange={e => setColor(e.target.value || null)}
              >
                {COLOR_OPTIONS.map(c => (
                  <option key={c.value || 'auto'} value={c.value || ''}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Audience */}
          <AudiencePicker value={audience} onChange={setAudience} />

          {/* Actions */}
          <div className="modal-action">
            {isEditing && onDelete && (
              <button
                type="button"
                className="btn btn-error btn-outline mr-auto"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? <span className="loading loading-spinner loading-sm" /> : (isEditing ? 'Save' : 'Create')}
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
}
